import React, { useState } from 'react';
import { Star } from 'lucide-react';

interface PastWinner {
  category: string;
  winners: string[];
  extra?: string;
}

interface PastEdition {
  year: number;
  winners: PastWinner[];
}

const PAST_EDITIONS: PastEdition[] = [
  {
    year: 2024,
    winners: [
      {
        category: 'Persona Innovadora del Año',
        winners: ['Pilar Batet Jiménez', 'Lorena Pulido Ramírez'],
      },
      {
        category: 'Mujer destacada del Sector Público',
        winners: ['Diana Arenas Blanco'],
      },
      {
        category: 'Nuevo talento público',
        winners: ['Pilar Moreno García'],
      },
      {
        category: 'Buen Gobierno',
        winners: ['Sistema de Integridad de Ciudad'],
        extra: 'Ayuntamiento de Sant Boi (España)',
      },
      {
        category: 'Categoría especial',
        winners: ['Víctor Almonacid Lamelas'],
      },
      {
        category: 'Proyecto más transformador',
        winners: ['POWERTY — Energías renovables para colectivos vulnerables'],
        extra: 'Agencia Andaluza de la Energía · Junta de Andalucía',
      },
      {
        category: 'Eficiencia en las AA.PP.',
        winners: [
          'Reingeniería Evolutiva: Transformando la DGR hacia un Modelo Integral y Digital',
        ],
        extra:
          'Dirección General de Rentas de la Provincia de Corrientes (Argentina)',
      },
      {
        category: 'Transformación Digital',
        winners: [
          'ACERCAR: Atención y cuidados para el entorno rural, compartido, amigable y revitalizado',
        ],
        extra: 'Gobierno de Navarra (España)',
      },
      {
        category: 'Gestión de personas',
        winners: [
          'TRANSFER. Metodología para la transferencia de conocimiento en el puesto de trabajo',
        ],
        extra: 'Instituto Andaluz de Administración Pública (España)',
      },
      {
        category: 'Creatividad en la innovación',
        winners: ['Ecosistema de innovación pública'],
        extra: 'Escuela de Administración Pública de Catalunya',
      },
    ],
  },
  {
    year: 2023,
    winners: [
      {
        category: 'Excelencia de la Comunidad NovaGob',
        winners: ['Joaquín Meseguer Yebra'],
      },
      {
        category: 'Mujer destacada del Sector Público',
        winners: ['Carmen Herrera Pardo'],
      },
      {
        category: 'Nuevo talento público',
        winners: ['Cristina Pérez Galán'],
      },
      {
        category: 'Categoría especial',
        winners: ['Xavier Marcet'],
      },
      {
        category: 'Creatividad en la innovación',
        winners: ['IA — Gobierno Fácil'],
        extra: 'Gobierno del Estado de Guanajuato',
      },
      {
        category: 'Proyecto más transformador',
        winners: [
          'Poner a las personas en el centro de las políticas: Servicios Digitales de Aragón',
        ],
      },
      {
        category: 'Eficiencia en las AA.PP.',
        winners: ['Comprometidos con las personas mayores'],
        extra: 'Cabildo Insular de Tenerife',
      },
      {
        category: 'Mejor idea aún no desarrollada',
        winners: ['Croquetas de conocimiento'],
        extra: 'Junta de Andalucía',
      },
      {
        category: 'Gestión de personas',
        winners: [
          'ANALIZ@. El impacto de un cuadro de mandos integral en la gestión de RRHH',
        ],
        extra: 'Diputación de Sevilla',
      },
    ],
  },
  {
    year: 2022,
    winners: [
      {
        category: 'Excelencia de la Comunidad NovaGob',
        winners: ['Pilar Moreno García'],
      },
      {
        category: 'Mujer destacada del Sector Público',
        winners: ['María Teresa Covisa Rubia'],
      },
      {
        category: 'Nuevo talento público',
        winners: ['Cristina Molina Suárez'],
      },
      {
        category: 'Categoría especial',
        winners: ['Xavier Marcet'],
      },
      {
        category: 'Creatividad en la innovación',
        winners: [
          'Proyecto de innovación sostenible con foco en las personas',
        ],
        extra: 'Laboratorio de Gobierno de Chile',
      },
      {
        category: 'Proyecto más transformador',
        winners: ['Análisis del volcán de La Palma con el uso de datos abiertos'],
      },
      {
        category: 'Excelencia a título póstumo',
        winners: ['Andrés Pastor'],
      },
      {
        category: 'Gestión de personas',
        winners: ['Canal Podcast de la ECLAP'],
      },
    ],
  },
  {
    year: 2020,
    winners: [
      {
        category: 'Excelencia de la Comunidad NovaGob',
        winners: ['Carles Ramió', 'Borja Colón'],
      },
      {
        category: 'Mujer destacada del Sector Público',
        winners: ['Carmen Seisdedos'],
      },
      {
        category:
          'Mejor proyecto interno en respuesta a COVID-19',
        winners: ['Empieza el Día con el IAAP y otras recetas covid'],
        extra: 'Instituto Andaluz de Administración Pública (IAAP)',
      },
      {
        category:
          'Mejor proyecto con impacto social en respuesta a COVID-19',
        winners: ['Protegiendo la salud con transparencia'],
        extra: 'Junta de Castilla y León',
      },
    ],
  },
];

const PastEditionsSection: React.FC = () => {
  const [activeYear, setActiveYear] = useState<number>(PAST_EDITIONS[0].year);
  const active =
    PAST_EDITIONS.find((e) => e.year === activeYear) || PAST_EDITIONS[0];

  return (
    <section id="ediciones-anteriores" className="landing-section landing-past">
      <div className="landing-container">
        <h2 className="landing-section-title text-gold-shine">
          Ediciones anteriores
        </h2>
        <p className="landing-section-lead">
          La memoria viva de los Premios NovaGob Excelencia: personas,
          proyectos e instituciones que marcaron cada edición.
        </p>

        <div
          className="past-tabs"
          role="tablist"
          aria-label="Selecciona una edición anterior"
        >
          {PAST_EDITIONS.map((edition) => {
            const isActive = edition.year === activeYear;
            return (
              <button
                key={edition.year}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`past-tab${isActive ? ' active' : ''}`}
                onClick={() => setActiveYear(edition.year)}
              >
                {edition.year}
              </button>
            );
          })}
        </div>

        <div className="past-grid" key={active.year}>
          {active.winners.map((w, i) => (
            <article className="past-card" key={`${active.year}-${i}`}>
              <Star
                className="past-card-bgstar"
                size={300}
                fill="currentColor"
                strokeWidth={0}
                aria-hidden="true"
              />
              <Star
                size={28}
                className="past-card-icon"
                fill="#FFD702"
                strokeWidth={1}
              />
              <h4 className="past-card-category">{w.category}</h4>
              <p className="past-card-winner">
                {w.winners.map((name, idx) => (
                  <span key={idx}>
                    {name}
                    {idx < w.winners.length - 1 && <br />}
                  </span>
                ))}
              </p>
              {w.extra && <p className="past-card-extra">{w.extra}</p>}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PastEditionsSection;
